import { OTP_VALIDITY_SECONDS, RESEND_COOLDOWN_SECONDS } from '@cibli/shared';
import { useTranslation } from 'react-i18next';

import { emailOtp, phoneNumber } from '@/lib/auth-client';
import { checkOtpStatus } from '@/services/otp';

export class OTPRateLimitError extends Error {
  constructor(public waitTimeSeconds: number) {
    super(`Rate limit exceeded. Please wait ${waitTimeSeconds} seconds.`);
    this.name = 'OTPRateLimitError';
  }
}

export type OTPSendOptions = {
  identifier: string;
  isEmail: boolean;
  /**
   * Check if an OTP already exists before sending
   * @default true
   */
  checkExisting?: boolean;
  /**
   * Enforce rate limiting (60s cooldown between sends)
   * @default true
   */
  enforceRateLimit?: boolean;
  /**
   * Resend cooldown in seconds used when enforceRateLimit is true
   * @default RESEND_COOLDOWN_SECONDS
   */
  rateLimitSeconds?: number;
};

export type OTPSendResult = {
  /**
   * Whether an existing OTP was found and is still valid
   */
  hasExistingOtp: boolean;
  /**
   * Time left in seconds for the existing OTP (if any)
   */
  timeLeft: number;
  /**
   * Whether a new OTP was sent (false if existing OTP was used)
   */
  wasSent: boolean;
};

/**
 * Hook to handle OTP sending with rate limiting and error handling.
 * Centralizes the logic for sending OTP via email or phone number.
 */
export function useOTPSender() {
  const { t } = useTranslation();

  /**
   * Send an OTP to the given identifier (email or phone number)
   * Handles rate limiting, existing OTP checks, and error translation
   *
   * @throws {OTPRateLimitError} If rate limit is exceeded
   * @throws {Error} If OTP sending fails
   */
  const sendOTP = async ({
    identifier,
    isEmail,
    checkExisting = true,
    enforceRateLimit = true,
    rateLimitSeconds = RESEND_COOLDOWN_SECONDS,
  }: OTPSendOptions): Promise<OTPSendResult> => {
    // 1. Check for existing OTP and enforce rate limiting if requested
    if (checkExisting) {
      const existingOTP = await checkOtpStatus(identifier, isEmail);

      if (existingOTP.exists) {
        const timeSinceLastSend = OTP_VALIDITY_SECONDS - existingOTP.timeLeft;

        if (enforceRateLimit && timeSinceLastSend < rateLimitSeconds) {
          const waitTime = rateLimitSeconds - timeSinceLastSend;
          throw new OTPRateLimitError(waitTime);
        }

        // Reuse a still-valid OTP when we deliberately skip rate limiting
        if (!enforceRateLimit && existingOTP.timeLeft > 0) {
          return {
            hasExistingOtp: true,
            timeLeft: existingOTP.timeLeft,
            wasSent: false,
          };
        }
      }
    }

    // 3. Send new OTP based on identifier type
    try {
      if (isEmail) {
        const { error: otpError } = await emailOtp.sendVerificationOtp({
          email: identifier,
          type: 'sign-in',
        });

        if (otpError) {
          throw otpError;
        }
      } else {
        const { error: otpError } = await phoneNumber.sendOtp({
          phoneNumber: identifier,
        });

        if (otpError) {
          throw otpError;
        }
      }

      return {
        hasExistingOtp: false, // a fresh OTP has just been sent
        timeLeft: OTP_VALIDITY_SECONDS,
        wasSent: true,
      };
    } catch (error: unknown) {
      // Translate error code to user-friendly message
      const errorCode = (error as { code?: string })?.code || 'OTP_SEND_FAILED';
      const errorMessage =
        t(`errors.${errorCode}`) !== `errors.${errorCode}`
          ? t(`errors.${errorCode}`)
          : t('formErrors.resendFailed');

      throw new Error(errorMessage);
    }
  };

  return { sendOTP };
}
